import { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/utils';

interface IPageTemplateProps extends HTMLAttributes<HTMLDivElement> {
  title: string;
  rightActions?: ReactNode;
  headerClassName?: string;
  contentClassName?: string;
  secondaryActions?: ReactNode;
  /**
   * If true, uses flex layout where page fills viewport height
   * with sticky header and scrollable content area
   */
  useStickyHeader?: boolean;
}

export const PageTemplate = ({
  title,
  rightActions,
  children,
  useStickyHeader = false,
  className,
  headerClassName,
  contentClassName,
  secondaryActions,
  ...props
}: IPageTemplateProps) => {
  if (useStickyHeader) {
    return (
      <div
        className={cn('flex h-full w-full flex-col overflow-hidden px-4 pb-6 pt-3', className)}
        {...props}
      >
        {/* Sticky header — wraps title row + secondary actions so background covers both */}
        <div className={cn('shrink-0', headerClassName)}>
          <div className="flex w-full items-center justify-between pb-4">
            <h1 className="min-w-0 truncate text-2xl font-semibold">{title}</h1>
            {rightActions && <div>{rightActions}</div>}
          </div>
          {secondaryActions && <div>{secondaryActions}</div>}
        </div>

        {/* Scrollable content area */}
        <div className={cn('flex min-h-0 flex-1 flex-col overflow-hidden', contentClassName)}>
          {children}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn('flex h-full w-full flex-col gap-4 overflow-y-auto px-4 pb-6 pt-3', className)}
      {...props}
    >
      <div className="align-center flex w-full justify-between">
        <h1 className="text-2xl font-semibold">{title}</h1>
        {rightActions && <div>{rightActions}</div>}
      </div>

      <div className="w-full">{children}</div>
    </div>
  );
};
